/**
 * CDN Configuration
 * 
 * Centralizes CDN settings for caching external resources via nginx proxy.
 * The nginx server at storage.interlaced.app caches:
 * - Google Tag Manager scripts
 * - Supabase profile photos (public and signed)
 * - Google Fonts (CSS and WOFF2 files)
 */

export const CDN_CONFIG = {
  /**
   * Enable/disable CDN URL rewriting
   * Can be toggled for debugging or gradual rollout
   */
  enabled: true,
  
  /**
   * Base URL for CDN proxy
   */
  baseUrl: 'https://storage.interlaced.app',
  
  /**
   * Supabase project URL (for URL matching)
   */
  supabaseUrl: 'https://ohllmsaklzfbepnivvfc.supabase.co',
  
  /**
   * CDN paths for different resource types
   */
  paths: {
    /** Signed profile photos - cached by path, token ignored */
    signedPhotos: '/signed-photos/',
    /** Public profile photos */
    publicPhotos: '/photos/',
    /** Google Fonts CSS */
    fonts: '/fonts/',
    /** Google Fonts static files (WOFF2) */
    gstatic: '/gstatic/',
    /** Google Tag Manager */
    gtm: '/gtm/'
  }
} as const;

/**
 * Convert a Supabase signed URL to CDN URL
 * 
 * Input:  https://ohllmsaklzfbepnivvfc.supabase.co/storage/v1/object/sign/profile_photos/abc/def_0.jpg?token=xxx
 * Output: https://storage.interlaced.app/signed-photos/abc/def_0.jpg?token=xxx
 * 
 * The nginx proxy caches by path only (ignores token), so different tokens
 * for the same image will hit the same cache entry.
 */
export function convertToCdnUrl(supabaseSignedUrl: string): string {
  if (!CDN_CONFIG.enabled) {
    return supabaseSignedUrl;
  }
  
  if (!supabaseSignedUrl || typeof supabaseSignedUrl !== 'string') {
    return supabaseSignedUrl;
  }
  
  // Only convert Supabase signed URLs
  if (!supabaseSignedUrl.includes('.supabase.co/storage/v1/object/sign/profile_photos/')) {
    return supabaseSignedUrl;
  }
  
  try {
    // Extract path and token from signed URL
    // Format: .../storage/v1/object/sign/profile_photos/abc/def_0.jpg?token=xxx
    const match = supabaseSignedUrl.match(/\/storage\/v1\/object\/sign\/profile_photos\/(.+)$/);
    
    if (!match) {
      return supabaseSignedUrl;
    }
    
    const pathWithToken = match[1]; // abc/def_0.jpg?token=xxx
    const cdnUrl = `${CDN_CONFIG.baseUrl}${CDN_CONFIG.paths.signedPhotos}${pathWithToken}`;
    
    return cdnUrl;
  } catch {
    return supabaseSignedUrl;
  }
}

/**
 * Check if a URL is already a CDN URL
 */
export function isCdnUrl(url: string): boolean {
  return url?.startsWith(CDN_CONFIG.baseUrl) ?? false;
}
